import React, { useState } from 'react';
import { useApp, PrescriptionItem, formatHumaneRovitaLicenseId } from './AppContext';
import { DigitalPrescriptionModal } from './DigitalPrescriptionModal';

interface DigitalPrescriptionGeneratorModalProps {
  patientId: string;
  patientName: string;
  patientMrn?: string;
  patientAge?: string;
  patientGender?: string;
  patientAllergies?: string;
  appointmentId?: string;
  initialDiagnosis?: string;
  initialNotes?: string;
  onClose: () => void;
  onPrescriptionIssued?: (rxId: string, items?: PrescriptionItem[]) => void;
}

// Common clinical medication presets for fast physician entry
const COMMON_MEDICATIONS = [
  { name: 'Amoxicillin + Clavulanic Acid', dosage: '625 mg', form: 'Tablet', frequency: 'Twice Daily (BID)', duration: '7 Days', instructions: 'Take with food or milk' },
  { name: 'Azithromycin', dosage: '500 mg', form: 'Tablet', frequency: 'Once Daily (OD)', duration: '3 Days', instructions: 'Take 1 hour before or 2 hours after meals' },
  { name: 'Paracetamol', dosage: '500 mg', form: 'Tablet', frequency: 'Every 6-8 Hours (PRN)', duration: '5 Days', instructions: 'Take as needed for pain or fever (max 4g/day)' },
  { name: 'Ibuprofen', dosage: '400 mg', form: 'Tablet', frequency: 'Three Times Daily (TID)', duration: '5 Days', instructions: 'Take strictly after meals with full glass of water' },
  { name: 'Omeprazole', dosage: '20 mg', form: 'Capsule', frequency: 'Once Daily (OD)', duration: '14 Days', instructions: 'Take in morning 30 minutes before breakfast' },
  { name: 'Cetirizine HCl', dosage: '10 mg', form: 'Tablet', frequency: 'Once Daily at Bedtime (HS)', duration: '7 Days', instructions: 'May cause mild drowsiness' },
  { name: 'Salbutamol Inhaler (100mcg/dose)', dosage: '2 Puffs', form: 'Inhaler', frequency: 'Every 4-6 Hours (PRN)', duration: '30 Days', instructions: 'Inhale when experiencing acute shortness of breath' },
  { name: 'Amlodipine Besylate', dosage: '5 mg', form: 'Tablet', frequency: 'Once Daily (OD)', duration: '30 Days', instructions: 'Take morning with or without food' },
  { name: 'Metformin HCl', dosage: '500 mg', form: 'Tablet', frequency: 'Twice Daily (BID)', duration: '30 Days', instructions: 'Take with morning and evening meals' },
  { name: 'Ciprofloxacin', dosage: '500 mg', form: 'Tablet', frequency: 'Twice Daily (BID)', duration: '5 Days', instructions: 'Drink plenty of fluids; avoid dairy 2 hours before/after' }
];

export const DigitalPrescriptionGeneratorModal: React.FC<DigitalPrescriptionGeneratorModalProps> = ({
  patientId,
  patientName,
  patientMrn,
  patientAge,
  patientGender,
  patientAllergies,
  appointmentId,
  initialDiagnosis = '',
  initialNotes = '',
  onClose,
  onPrescriptionIssued
}) => {
  const { user, issueDigitalPrescription, showToast } = useApp();

  const [diagnosis, setDiagnosis] = useState(initialDiagnosis || '');
  const [allergies, setAllergies] = useState(patientAllergies || 'No Known Drug Allergies (NKDA)');
  const [treatmentPlan, setTreatmentPlan] = useState('');
  const [dietaryAdvice, setDietaryAdvice] = useState('');
  const [generalPrecautions, setGeneralPrecautions] = useState('');
  const [followUpInstructions, setFollowUpInstructions] = useState('Follow up in 7 days or if symptoms worsen.');
  const [notes, setNotes] = useState(initialNotes || '');
  const [doctorLicenseNumber, setDoctorLicenseNumber] = useState<string>(() => user?.licenseNumber || user?.medicalLicenseNumber || user?.rovitaLicenseId || formatHumaneRovitaLicenseId(user));
  
  // Structured medication items
  const [items, setItems] = useState<PrescriptionItem[]>([
    {
      id: 'item-1',
      name: '',
      dosage: '',
      form: 'Tablet',
      frequency: 'Twice Daily (BID)',
      duration: '5 Days',
      instructions: 'Take after meals with water',
      dispenseQuantity: ''
    }
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLivePreview, setShowLivePreview] = useState(false);

  // Form options
  const FORM_OPTIONS = ['Tablet', 'Capsule', 'Syrup / Suspension', 'Drops', 'Inhaler', 'Topical Cream / Ointment', 'Injection', 'Suppository', 'Sachet'];
  const FREQUENCY_OPTIONS = [
    'Once Daily (OD)',
    'Twice Daily (BID)',
    'Three Times Daily (TID)',
    'Four Times Daily (QID)',
    'Every 8 Hours',
    'Every 12 Hours',
    'Once Daily at Bedtime (HS)',
    'As Needed (PRN) for symptoms',
    'Alternate Days'
  ];
  const DURATION_OPTIONS = ['3 Days', '5 Days', '7 Days', '10 Days', '14 Days', '21 Days', '30 Days', '60 Days', '90 Days', 'Ongoing'];

  const handleAddItem = () => {
    setItems(prev => [
      ...prev,
      {
        id: `item-${Date.now()}`,
        name: '',
        dosage: '',
        form: 'Tablet',
        frequency: 'Twice Daily (BID)',
        duration: '5 Days',
        instructions: 'Take after meals with water',
        dispenseQuantity: ''
      }
    ]);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length <= 1) {
      // If only one, just clear it
      setItems([{
        id: 'item-1',
        name: '',
        dosage: '',
        form: 'Tablet',
        frequency: 'Twice Daily (BID)',
        duration: '5 Days',
        instructions: '',
        dispenseQuantity: ''
      }]);
      return;
    }
    setItems(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleItemChange = (index: number, field: keyof PrescriptionItem, val: string) => {
    setItems(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: val };
      return copy;
    });
  };

  const handleApplyPreset = (preset: typeof COMMON_MEDICATIONS[0]) => {
    // If the last item is empty, replace it, otherwise append
    setItems(prev => {
      const last = prev[prev.length - 1];
      if (prev.length === 1 && !last.name.trim()) {
        return [{
          id: 'item-1',
          ...preset
        }];
      }
      return [
        ...prev,
        {
          id: `item-${Date.now()}`,
          ...preset
        }
      ];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!diagnosis.trim()) {
      showToast('Diagnosis Required', 'Please enter a primary clinical diagnosis before issuing.', 'warning');
      return;
    }

    const validItems = items.filter(it => it.name.trim().length > 0);
    if (validItems.length === 0) {
      showToast('Medication Required', 'Please add at least one medication to the prescription.', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const rxId = await issueDigitalPrescription({
        patientId,
        patientName,
        patientMrn,
        patientAge,
        patientGender,
        patientAllergies: allergies,
        diagnosis,
        items: validItems,
        treatmentPlan,
        dietaryAdvice,
        generalPrecautions,
        followUpInstructions,
        notes,
        appointmentId,
        doctorLicenseNumber: doctorLicenseNumber.trim() || formatHumaneRovitaLicenseId(user)
      } as any);

      if (onPrescriptionIssued) {
        onPrescriptionIssued(rxId, validItems);
      }
      onClose();
    } catch (err: any) {
      console.error("Failed to issue digital prescription:", err);
      showToast('Error', err.message || 'Failed to issue digital prescription.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[350] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto font-['Plus_Jakarta_Sans'] animate-in fade-in duration-200">
        <div className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden my-auto flex flex-col border border-slate-200 max-h-[90vh]">
          
          {/* Header */}
          <div className="p-5 sm:p-6 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800">
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-600/30">
                <i className="fa-solid fa-prescription-bottle-medical text-xl"></i>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Clinical Consultation Tool</span>
                  <span className="text-[10px] bg-indigo-500/20 text-indigo-300 font-bold px-2 py-0.5 rounded-full border border-indigo-500/30">Rovita e-Rx</span>
                </div>
                <h2 className="text-base sm:text-lg font-black text-white">Generate Digital Prescription</h2>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowLivePreview(true)}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-indigo-300 hover:text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 active:scale-95"
                title="Preview Official Document with Rovita Branding"
              >
                <i className="fa-solid fa-eye"></i>
                <span className="hidden sm:inline">Preview Document</span>
              </button>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-all"
                aria-label="Close prescription creator"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 sm:p-8 space-y-6 text-slate-900">
            
            {/* Patient Bar Overview */}
            <div className="p-4 bg-indigo-50/60 rounded-2xl border border-indigo-100 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold">
                  {patientName.charAt(0)}
                </div>
                <div>
                  <p className="font-black text-slate-900 text-sm">{patientName}</p>
                  <p className="text-[11px] text-slate-500 font-medium">
                    {patientMrn || 'ROVITA PATIENT'} • {patientAge || 'Adult'} • {patientGender || 'Unspecified'}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-slate-500">Prescribing:</span>
                  <span className="font-bold text-slate-800 bg-white px-2.5 py-1 rounded-lg border border-slate-200">
                    {user?.name?.toLowerCase().startsWith('dr') ? user.name : `Dr. ${user?.name || 'Practitioner'}`}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <label htmlFor="rx-doc-license" className="text-[11px] font-semibold text-slate-500">License ID:</label>
                  <input
                    id="rx-doc-license"
                    type="text"
                    value={doctorLicenseNumber}
                    onChange={e => setDoctorLicenseNumber(e.target.value)}
                    placeholder="e.g. MD-84920"
                    className="font-mono text-[11px] font-bold text-slate-800 bg-white px-2 py-1 rounded-lg border border-slate-200 focus:border-indigo-500 outline-none w-36"
                    title="Prescribing Physician License ID / Board Number"
                  />
                </div>
              </div>
            </div>

            {/* Clinical Diagnosis & Allergies */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <i className="fa-solid fa-stethoscope text-indigo-600"></i>
                  Clinical Diagnosis / Primary Indication <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Acute Pharyngitis, Essential Hypertension, Migraine..."
                  value={diagnosis}
                  onChange={e => setDiagnosis(e.target.value)}
                  className="w-full p-3 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-800"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <i className="fa-solid fa-shield-virus text-rose-500"></i>
                  Known Drug Allergies / Contraindications
                </label>
                <input
                  type="text"
                  placeholder="e.g. NKDA (No known drug allergies), Penicillin, Sulfa drugs..."
                  value={allergies}
                  onChange={e => setAllergies(e.target.value)}
                  className="w-full p-3 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-sm font-semibold text-slate-800"
                />
              </div>
            </div>

            {/* Quick Medication Suggestions Bar */}
            <div className="space-y-2">
              <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <i className="fa-solid fa-bolt text-amber-500"></i>
                Quick Clinical Formulary Presets (1-Click Add):
              </label>
              <div className="flex flex-wrap gap-1.5">
                {COMMON_MEDICATIONS.map((med, idx) => (
                  <button
                    type="button"
                    key={idx}
                    onClick={() => handleApplyPreset(med)}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-indigo-50 hover:border-indigo-200 border border-slate-200 rounded-lg text-[11px] font-semibold text-slate-700 hover:text-indigo-700 transition-all flex items-center gap-1"
                  >
                    <i className="fa-solid fa-plus text-[9px] text-indigo-500"></i>
                    {med.name.split(' ')[0]} ({med.dosage})
                  </button>
                ))}
              </div>
            </div>

            {/* Prescribed Medications Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-serif font-black text-indigo-600">℞</span>
                  <label className="text-xs font-black uppercase tracking-wider text-slate-900">
                    Prescribed Medications ({items.length})
                  </label>
                </div>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 active:scale-95"
                >
                  <i className="fa-solid fa-plus"></i> Add Another Drug
                </button>
              </div>

              {/* Medication Item Cards */}
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={item.id || index} className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3 relative group">
                    <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                      <span className="text-xs font-bold text-indigo-600 flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-black flex items-center justify-center">
                          {index + 1}
                        </span>
                        Medication Details
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-slate-400 hover:text-rose-600 text-xs font-bold flex items-center gap-1 transition-colors"
                        title="Remove drug"
                      >
                        <i className="fa-solid fa-trash-can"></i>
                        <span className="text-[10px]">Remove</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2 space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Drug / Generic Name *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Amoxicillin, Paracetamol..."
                          value={item.name}
                          onChange={e => handleItemChange(index, 'name', e.target.value)}
                          className="w-full p-2.5 bg-white rounded-xl border border-slate-300 focus:border-indigo-500 outline-none text-xs font-bold text-slate-800"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Formulation</label>
                        <select
                          value={item.form}
                          onChange={e => handleItemChange(index, 'form', e.target.value)}
                          className="w-full p-2.5 bg-white rounded-xl border border-slate-300 focus:border-indigo-500 outline-none text-xs font-semibold text-slate-800"
                        >
                          {FORM_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Dosage / Strength</label>
                        <input
                          type="text"
                          placeholder="e.g. 500 mg, 10 ml, 2 puffs"
                          value={item.dosage}
                          onChange={e => handleItemChange(index, 'dosage', e.target.value)}
                          className="w-full p-2.5 bg-white rounded-xl border border-slate-300 focus:border-indigo-500 outline-none text-xs font-medium text-slate-800"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Frequency / Schedule</label>
                        <select
                          value={item.frequency}
                          onChange={e => handleItemChange(index, 'frequency', e.target.value)}
                          className="w-full p-2.5 bg-white rounded-xl border border-slate-300 focus:border-indigo-500 outline-none text-xs font-semibold text-slate-800"
                        >
                          {FREQUENCY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Duration</label>
                        <select
                          value={item.duration}
                          onChange={e => handleItemChange(index, 'duration', e.target.value)}
                          className="w-full p-2.5 bg-white rounded-xl border border-slate-300 focus:border-indigo-500 outline-none text-xs font-semibold text-slate-800"
                        >
                          {DURATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2 space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Specific Patient Instructions</label>
                        <input
                          type="text"
                          placeholder="e.g. Take after breakfast and dinner with water, finish full course..."
                          value={item.instructions}
                          onChange={e => handleItemChange(index, 'instructions', e.target.value)}
                          className="w-full p-2.5 bg-white rounded-xl border border-slate-300 focus:border-indigo-500 outline-none text-xs font-medium text-slate-800"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-500 uppercase">Dispense Qty (Optional)</label>
                        <input
                          type="text"
                          placeholder="e.g. 14 Tablets, 1 Bottle"
                          value={item.dispenseQuantity}
                          onChange={e => handleItemChange(index, 'dispenseQuantity', e.target.value)}
                          className="w-full p-2.5 bg-white rounded-xl border border-slate-300 focus:border-indigo-500 outline-none text-xs font-medium text-slate-800"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Non-Pharmacological Advice, Diet, and Precautions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <i className="fa-solid fa-utensils text-amber-500"></i>
                  Dietary & Lifestyle Advice
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Adequate hydration (2.5L water/day), avoid spicy/acidic foods..."
                  value={dietaryAdvice}
                  onChange={e => setDietaryAdvice(e.target.value)}
                  className="w-full p-3 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-xs font-medium text-slate-800 resize-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <i className="fa-solid fa-triangle-exclamation text-rose-500"></i>
                  Precautions & Follow-up Instructions
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Rest for 48 hours. Follow up in 7 days or seek emergency care if high fever persists..."
                  value={followUpInstructions}
                  onChange={e => setFollowUpInstructions(e.target.value)}
                  className="w-full p-3 bg-slate-50 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 outline-none text-xs font-medium text-slate-800 resize-none"
                />
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <i className="fa-solid fa-circle-info text-indigo-600"></i>
                <span>This digital prescription will be immediately transmitted and accessible in the patient's record.</span>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-1/2 sm:w-auto px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl text-xs font-bold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-1/2 sm:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-spinner animate-spin"></i>
                      <span>Issuing e-Rx...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane"></i>
                      <span>Issue & Transmit to Patient</span>
                    </>
                  )}
                </button>
              </div>
            </div>

          </form>

        </div>
      </div>

      {/* Live Preview Modal */}
      {showLivePreview && (
        <DigitalPrescriptionModal
          customData={{
            rxId: `ROVITA-RX-${new Date().getFullYear()}-PREVIEW`,
            date: new Date().toLocaleDateString(),
            issuedAt: new Date().toISOString(),
            doctorName: user?.name?.toLowerCase().startsWith('dr') ? user.name : `Dr. ${user?.name || 'Practitioner'}`,
            doctorSpecialty: user?.specialties?.[0] || 'Telemedicine Practitioner',
            doctorLicenseNumber: doctorLicenseNumber.trim() || formatHumaneRovitaLicenseId(user),
            patientName: patientName,
            patientMrn: patientMrn || 'ROVITA-PT-CURRENT',
            patientAge: patientAge || 'Adult',
            patientGender: patientGender || 'Unspecified',
            patientAllergies: allergies,
            diagnosis: diagnosis || 'Clinical Consultation',
            items: items.filter(it => it.name.trim().length > 0),
            dietaryAdvice: dietaryAdvice,
            generalPrecautions: generalPrecautions,
            followUpInstructions: followUpInstructions
          }}
          onClose={() => setShowLivePreview(false)}
        />
      )}
    </>
  );
};
